import { VaporPageSection, Box, HeaderBar, IconButton, VaporPage, Card } from '@vapor/material'
import { FC, useMemo } from 'react'
import { ArrowBack } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { useMediaQuery } from '@mui/material'
import { BoxProps, CSSPseudoSelectorProps, CSSSelectorObjectOrCssVariables, SystemCssProperties, Theme, useTheme } from '@mui/system'
import React from 'react'
import { HtmlElementId } from 'src/app/layout/html-element-id.type'

/** ++++++++++++++++++++++++++++++++++++++++++++ */
// TEMP STYLED COMPONENTS TO FIX ISSUES OF VAPOR

type HicVaporPageProps = BoxProps & {
  appBarVisible?: boolean
  fullWidth?: boolean
}

export const PAGE_WIDTH = '1280px'

const HicVaporPage: React.FC<HicVaporPageProps> = ({ appBarVisible, fullWidth, ...rest }) => {
  const theme = useTheme()
  const componentStyles: SystemCssProperties<Theme> | CSSPseudoSelectorProps<Theme> | CSSSelectorObjectOrCssVariables<Theme> | React.CSSProperties = {
    height: `calc(100vh - ${appBarVisible ? 120 : 48}px)`,
    overflow: 'auto',
    'div:has(> .mainCard)': {
      padding: '20px!important',
      paddingBottom: '0px!important',
      boxSizing: 'border-box',
      display: 'flex',
      [theme.breakpoints.down('xl')]: {
        display: 'block',
        padding: '0px!important',
      },
      width: '100%',
      justifyContent: 'center',
      flexGrow: 1,
    },

    '.mainCard': {
      [theme.breakpoints.down('xl')]: {
        maxWidth: '100vw',
      },
      maxWidth: fullWidth ? '100vw' : PAGE_WIDTH,
      flexGrow: 1,
    },
  }

  return <Box sx={componentStyles} {...rest} />
}

//#region old
// const HicVaporPage2 = styled(Box)<{ appBarVisible?: boolean; withMargin: boolean; fullWidth?: boolean }>(({ theme, appBarVisible, withMargin, fullWidth }) => ({
//   height: `calc(100vh - ${props.appBarVisible ? 104 : 48}px)`,
//   overflow: 'auto',

//   'div:has(> .mainCard)': {
//     padding: '20px!important',
//     paddingBottom: '0px!important',
//     boxSizing: 'border-box',
//     display: 'flex',
//     [props.theme.breakpoints.down('xl')]: {
//       display: 'block',
//       padding: '0px!important',
//     },
//     width: '100%',
//     justifyContent: 'center',
//     flexGrow: 1,
//   },

//   '.mainCard': {
//     [props.theme.breakpoints.down('xl')]: {
//       maxWidth: '100%',
//     },
//     maxWidth: props.fullWidth ? '100%' : '1280px',
//     flexGrow: 1,
//   },
// }))
//#endregion

const calcMinHeight = (margin: number, xs: boolean, appBarVisible?: boolean, hideHeader?: boolean) => {
  const headBarHeight = 48
  const titleBarHeight = xs ? 48 : 80
  const appBarHeight = 72
  let heightToSubtract = headBarHeight + titleBarHeight + margin
  if (appBarVisible) {
    heightToSubtract = heightToSubtract + appBarHeight
  }
  if (hideHeader) {
    heightToSubtract = heightToSubtract - titleBarHeight
  }
  return `calc(100vh - ${Math.abs(heightToSubtract)}px)`
}

type HicSectionProps = BoxProps & {
  appBarVisible?: boolean
  hideHeader?: boolean
}

const HicSection: React.FC<HicSectionProps> = ({ appBarVisible, hideHeader, ...rest }) => {
  const theme = useTheme()

  const xs = !useMediaQuery(theme.breakpoints.up('sm'))

  const componentStyle: SystemCssProperties<Theme> | CSSPseudoSelectorProps<Theme> | CSSSelectorObjectOrCssVariables<Theme> | React.CSSProperties = {
    width: '100%',
    minHeight: calcMinHeight(0, xs, appBarVisible, hideHeader),
    [theme.breakpoints.up('lg')]: {
      minHeight: calcMinHeight(hideHeader ? 52 : 37, xs, appBarVisible, hideHeader),
      padding: '16px',
      paddingBottom: '1px',
    },
    [theme.breakpoints.between('md', 'lg')]: {
      minHeight: calcMinHeight(hideHeader ? 36 : 0, xs, appBarVisible, hideHeader),
      paddingBottom: '0px',
    },
    [theme.breakpoints.down('md')]: {
      minHeight: calcMinHeight(hideHeader ? 36 : 0, xs, appBarVisible, hideHeader),
      paddingBottom: '0px',
    },
  }

  return <Box sx={componentStyle} {...rest} />
}

// const HicSectionOLD = styled(Box)<{ appBarVisible?: boolean; withMargin: boolean; hideHeader?: boolean }>(({ appBarVisible, withMargin, hideHeader, ...props }) => {
//   const xs = !useMediaQuery(props.theme.breakpoints.up('sm'))
//   return {
//     width: '100%',
//     minHeight: calcMinHeight(0, xs, appBarVisible, hideHeader),
//     [props.theme.breakpoints.up('lg')]: {
//       minHeight: calcMinHeight(hideHeader ? 52 : 37, xs, appBarVisible, hideHeader),
//       padding: '16px',
//       paddingBottom: '1px',
//     },
//     [props.theme.breakpoints.between('md', 'lg')]: {
//       minHeight: calcMinHeight(hideHeader ? 36 : 0, xs, appBarVisible, hideHeader),
//       paddingBottom: '0px',
//     },
//     [props.theme.breakpoints.down('md')]: {
//       minHeight: calcMinHeight(hideHeader ? 36 : 0, xs, appBarVisible, hideHeader),
//       paddingBottom: '0px',
//     },
//   }
// })

/** ++++++++++++++++++++++++++++++++++++++++++++ */

export type PageProps = {
  title: string
  description?: string
  headerReduced?: boolean
  headerBack?: boolean
  appBar?: React.ReactElement
  hideHeader?: boolean
  fullWidth?: boolean
  leftItems?: React.ReactNode[]
  rightItems?: React.ReactNode[]
  customHeader?: JSX.Element
  removeMargin?: true
  id?: HtmlElementId
}

const Page: FC<PageProps> = (props) => {
  const navigate = useNavigate()
  const { sm, md, lg } = useBreakpoints()

  const appBarVisible = useMemo(() => {
    return typeof props.appBar !== 'undefined'
  }, [props.appBar])

  const pageContent = useMemo(() => {
    return (
      <div id={props.id ? props.id.toString() : undefined}>
        {props.hideHeader || Boolean(props.customHeader) ? undefined : (
          <HeaderBar
            size={sm ? 'large' : 'small'}
            divider
            title={props.title}
            description={props.description}
            reduced={props.headerReduced}
            leftItems={
              props.headerBack
                ? [
                    <IconButton color="primary" onClick={() => navigate(-1)}>
                      <ArrowBack />
                    </IconButton>,
                  ]
                : props.leftItems
            }
            rightItems={props.rightItems}
          />
        )}
        <VaporPageSection>
          <HicSection appBarVisible={appBarVisible} hideHeader={props.hideHeader || Boolean(props.customHeader)}>
            {props.customHeader}
            {props.children}
          </HicSection>
        </VaporPageSection>
      </div>
    )
  }, [props, sm, md, lg])

  return (
    <>
      <HicVaporPage appBarVisible={appBarVisible} fullWidth={props.fullWidth}>
        {!props.removeMargin && (
          <VaporPage variant="card">
            <Card className="mainCard" style={{ overflow: 'visible', height: '100%' }}>
              {pageContent}
            </Card>
          </VaporPage>
        )}
        {props.removeMargin && pageContent}
      </HicVaporPage>
      {props.appBar}
    </>
  )
}

export default Page
